<template>
  <v-card flat>
    <v-card-text>
      <div class="d-flex align-center pb-3 section-title-row">
        <h4 class="mb-0">処分終了報告</h4>
        <v-btn
          v-if="shouldShowInputSectionToggle"
          small
          :outlined="activeInputSection !== 'disposal'"
          :color="activeInputSection === 'disposal' ? 'primary' : 'default'"
          :disabled="!canEdit"
          @click="selectInputSection('disposal')"
        >
          {{ activeInputSection === 'disposal' ? '編集中' : '編集する' }}
        </v-btn>
      </div>
      <v-row dense>
        <v-col cols="12">
          <!--
            ・最終処分終了報告まで行われている場合は報告区分の変更は不可とする
            ・2次マニフェストの場合は「中間」から「最終」変更は不可とする
            ・法令施工後で紙マニフェスト以外は「中間」から「最終」変更は不可とする
          -->
          <v-radio-group
            v-model="manifestDisposal.disposal_report_type"
            :disabled="isDisposalReportTypeSelectionDisabled()"
            row
            dense
            label="報告区分"
            hide-details="auto"
            class="mt-0"
          >
            <v-radio
              v-for="n in disposalReportTypes"
              :key="n.value"
              :label="n.text"
              :value="n.value"
            />
          </v-radio-group>
        </v-col>
      </v-row>

      <!-- 処分担当者／処分終了日 -->
      <v-row>
        <v-col
          v-if="canEditDisposalReport"
          cols="12"
          md="6"
        >
          <validation-provider
            v-slot="{ errors }"
            class="requiredMark"
            name="処分担当者"
            :rules="canEditDisposalReport ? 'required|max:12' : ''"
          >
            <v-combobox
              v-model="manifestDisposal.disposal_report_person"
              :error-messages="errors"
              :items="users"
              :return-object="false"
              dense
              label="処分担当者"
              hide-details="auto"
              outlined
              persistent-placeholder
            >
              <template v-slot:label>
                <span>処分担当者</span>
                <span class="ml-1">
                  ({{ manifestDisposal.disposal_report_person?.length || 0 }} / 12)
                </span>
              </template>
              <template v-slot:counter></template>
            </v-combobox>
          </validation-provider>
        </v-col>
        <v-col
          v-else
          cols="12"
          md="6"
        >
          <validation-provider
            v-slot="{ errors }"
            class="requiredMark"
            name="処分担当者"
          >
            <v-text-field
              v-model="manifestDisposal.disposal_report_person"
              :error-messages="errors"
              disabled
              dense
              label="処分担当者"
              hide-details="auto"
              outlined
              persistent-placeholder
            >
              <template v-slot:label>
                <span>処分担当者</span>
                <span class="ml-1">
                  ({{ manifestDisposal.disposal_report_person?.length || 0 }} / 12)
                </span>
              </template>
              <template v-slot:counter></template>
            </v-text-field>
          </validation-provider>
        </v-col>

        <v-col cols="12" md="6">
          <cs-date-picker
            :date.sync="manifestDisposal.disposal_report_end_date"
            :disabled="!canEditDisposalReport"
            :minimum-date="manifestDraft.collected_date ? manifestDraft.collected_date : null"
            :maximum-date="manifestDisposal.disposal_report_end_report_date ? manifestDisposal.disposal_report_end_report_date : systemDate"
            @dateChangedCallback="(date) => {manifestDisposal.disposal_report_end_date = date}"
          >
            <template v-slot="{ on, attrs }">
              <validation-provider
                v-slot="{ errors }"
                class="requiredMark"
                name="処分終了日"
                :rules="canEditDisposalReport ? 'required' : ''"
              >
                <v-text-field
                  v-model="manifestDisposal.disposal_report_end_date"
                  v-bind="attrs"
                  v-on="on"
                  :disabled="!canEditDisposalReport"
                  :error-messages="errors"
                  append-icon="mdi-calendar"
                  clearable
                  dense
                  hide-details="auto"
                  label="処分終了日"
                  outlined
                  persistent-hint
                  persistent-placeholder
                  readonly
                />
              </validation-provider>
            </template>
          </cs-date-picker>
        </v-col>
      </v-row>
      <!-- /処分担当者／処分終了日 -->

      <!-- 報告担当者／運搬担当者 -->
      <v-row>
        <v-col
          v-if="canEditDisposalReport"
          cols="12"
          md="6"
        >
          <validation-provider
            v-slot="{ errors }"
            name="報告担当者"
            :rules="canEditDisposalReport ? 'max:12' : ''"
          >
            <v-combobox
              v-model="manifestDisposal.disposal_report_reporter"
              :error-messages="errors"
              :items="users"
              :return-object="false"
              dense
              label="報告担当者"
              hide-details="auto"
              outlined
              persistent-placeholder
            >
              <template v-slot:label>
                <span>報告担当者</span>
                <span class="ml-1">
                  ({{ manifestDisposal.disposal_report_reporter?.length || 0 }} / 12)
                </span>
              </template>
              <template v-slot:counter></template>
            </v-combobox>
          </validation-provider>
        </v-col>
        <v-col
          v-else
          cols="12"
          md="6"
        >
          <validation-provider
            v-slot="{ errors }"
            name="報告担当者"
          >
            <v-text-field
              v-model="manifestDisposal.disposal_report_reporter"
              :error-messages="errors"
              disabled
              dense
              label="報告担当者"
              hide-details="auto"
              outlined
              persistent-placeholder
            >
              <template v-slot:label>
                <span>報告担当者</span>
                <span class="ml-1">
                  ({{ manifestDisposal.disposal_report_reporter?.length || 0 }} / 12)
                </span>
              </template>
              <template v-slot:counter></template>
            </v-text-field>
          </validation-provider>
        </v-col>
        <v-col
          v-if="canEditDisposalReport"
          cols="12"
          md="6"
        >
          <validation-provider
            v-slot="{ errors }"
            name="運搬担当者"
            :rules="canEditDisposalReport ? 'max:12' : ''"
          >
            <v-combobox
              v-model="manifestDisposal.disposal_report_driver"
              :error-messages="errors"
              :items="drivers"
              :return-object="false"
              dense
              label="運搬担当者"
              hide-details="auto"
              outlined
              persistent-placeholder
            >
              <template v-slot:label>
                <span>運搬担当者</span>
                <span class="ml-1">
                  ({{ manifestDisposal.disposal_report_driver?.length || 0 }} / 12)
                </span>
              </template>
              <template v-slot:counter></template>
            </v-combobox>
          </validation-provider>
        </v-col>
        <v-col
          v-else
          cols="12"
          md="6"
        >
          <validation-provider
            v-slot="{ errors }"
            name="運搬担当者"
          >
            <v-text-field
              v-model="manifestDisposal.disposal_report_driver"
              :error-messages="errors"
              disabled
              dense
              label="運搬担当者"
              hide-details="auto"
              outlined
              persistent-placeholder
            >
              <template v-slot:label>
                <span>運搬担当者</span>
                <span class="ml-1">
                  ({{ manifestDisposal.disposal_report_driver?.length || 0 }} / 12)
                </span>
              </template>
              <template v-slot:counter></template>
            </v-text-field>
          </validation-provider>
        </v-col>
      </v-row>
      <!-- /報告担当者／運搬担当者 -->

      <!-- 車両番号／廃棄物受領日 -->
      <v-row>
        <v-col
          v-if="canEditDisposalReport"
          cols="12"
          md="6"
        >
          <validation-provider
            v-slot="{ errors }"
            name="車両番号"
            :rules="canEditDisposalReport ? 'max:15' : ''"
          >
            <v-combobox
              v-model="manifestDisposal.disposal_report_vehicle_number"
              :error-messages="errors"
              :items="vehicles"
              :return-object="false"
              :clearable="true"
              item-text="number"
              item-value="number"
              dense
              label="車両番号"
              hide-details="auto"
              outlined
              persistent-placeholder
            >
              <template v-slot:label>
                <span>車両番号</span>
                <span class="ml-1">
                  ({{ manifestDisposal.disposal_report_vehicle_number?.length || 0 }} / 15)
                </span>
              </template>
              <template v-slot:counter></template>
            </v-combobox>
          </validation-provider>
        </v-col>
        <v-col
          v-else
          cols="12"
          md="6"
        >
          <validation-provider
            v-slot="{ errors }"
            name="車両番号"
          >
            <v-text-field
              v-model="manifestDisposal.disposal_report_vehicle_number"
              :error-messages="errors"
              disabled
              dense
              label="車両番号"
              hide-details="auto"
              outlined
              persistent-placeholder
            >
              <template v-slot:label>
                <span>車両番号</span>
                <span class="ml-1">
                  ({{ manifestDisposal.disposal_report_vehicle_number?.length || 0 }} / 15)
                </span>
              </template>
              <template v-slot:counter></template>
            </v-text-field>
          </validation-provider>
        </v-col>
        <v-col cols="12" md="6">
          <cs-date-picker
            :date.sync="manifestDisposal.disposal_report_receipt_date"
            :disabled="!canEditDisposalReport"
            :minimum-date="manifestDraft.collected_date ? manifestDraft.collected_date : null"
            :maximum-date="systemDate"
            @dateChangedCallback="(date) => {manifestDisposal.disposal_report_receipt_date = date}"
          >
            <template v-slot="{ on, attrs }">
              <validation-provider
                v-slot="{ errors }"
                name="廃棄物受領日"
                :rules="canEditDisposalReport ? {disposalReportReceiptDateCheck: {disposal_report_end_date: manifestDisposal.disposal_report_end_date}} : {}"
              >
                <v-text-field
                  v-model="manifestDisposal.disposal_report_receipt_date"
                  v-bind="attrs"
                  v-on="on"
                  :disabled="!canEditDisposalReport"
                  :error-messages="errors"
                  append-icon="mdi-calendar"
                  clearable
                  dense
                  hide-details="auto"
                  label="廃棄物受領日"
                  outlined
                  persistent-hint
                  persistent-placeholder
                  readonly
                />
              </validation-provider>
            </template>
          </cs-date-picker>
        </v-col>
      </v-row>
      <!-- /車両番号／廃棄物受領日 -->

      <!-- 受入量／受入量の単位 -->
      <v-row>
        <v-col cols="12" md="6">
          <validation-provider
            v-slot="{ errors }"
            :class="canEditDisposalReport && (manifestDisposal.amount_require || manifestDisposal.disposal_report_amount_unit_id > 0) ? 'requiredMark' : ''"
            :rules="canEditDisposalReport ? {
              required: manifestDisposal.amount_require || manifestDisposal.disposal_report_amount_unit_id > 0,
              double: true,
              min_value: 0.001,
              max_value: 99999.999,
              quantityWithDecimal: { int_digits: 5, dec_digits: 3 }
            } : {}"
            :vid="`disposal_report_amount-${manifest.id}`"
            name="受入量"
            :custom-messages="customMessages"
          >
            <v-text-field
              v-model="manifestDisposal.disposal_report_amount"
              :disabled="!canEditDisposalReport"
              :error-messages="errors"
              dense
              hide-details="auto"
              label="受入量"
              outlined
              persistent-placeholder
              reverse
              type="number"
              @keydown.69.prevent
            >
            </v-text-field>
          </validation-provider>
        </v-col>
        <v-col cols="12" md="6">
          <validation-provider
            v-slot="{ errors }"
            :class="canEditDisposalReport && (manifestDisposal.amount_require || manifestDisposal.disposal_report_amount > 0) ? 'requiredMark' : ''"
            :rules="canEditDisposalReport ? { required: manifestDisposal.amount_require || manifestDisposal.disposal_report_amount > 0 } : {}"
            :vid="`disposal_report_amount_unit_id-${manifest.id}`"
            name="単位"
          >
            <v-select
              v-model="manifestDisposal.disposal_report_amount_unit_id"
              :disabled="!canEditDisposalReport"
              :error-messages="errors"
              :items="quantityUnits"
              clearable
              dense
              hide-details="auto"
              label="受入量の単位"
              outlined
              persistent-placeholder
              style="font-size: 100%"
            />
          </validation-provider>
        </v-col>
      </v-row>
      <!-- /受入量／受入量の単位 -->
      <!-- 処分備考 -->
      <v-row>
        <v-col cols="12">
          <validation-provider
            v-slot="{ errors }"
            name="処分備考"
            :rules="canEditDisposalReport ? 'max:128' : ''"
          >
            <v-text-field
              v-model="manifestDisposal.disposal_report_remarks"
              :disabled="!canEditDisposalReport"
              :error-messages="errors"
              dense
              hide-details="auto"
              label="処分備考"
              outlined
              persistent-placeholder
            >
              <template v-slot:label>
                <span>処分備考</span>
                <span class="ml-1">
                  ({{ manifestDisposal.disposal_report_remarks?.length || 0 }} / 128)
                </span>
              </template>
              <template v-slot:counter></template>
            </v-text-field>
          </validation-provider>
        </v-col>
      </v-row>
      <!-- /処分備考 -->

      <!-- 再資源化等情報 -->
      <v-row v-if="showRecycleDisposalReport">
        <v-col cols="12">
          <modify-recycle-disposal-report-basic
            :manifest="manifest"
            :manifest-disposal="manifestDisposal"
            :can-edit="canEditRecycleDisposalReport"
            :is-active="activeInputSection === 'recycle'"
            :can-toggle="canEdit"
            :show-toggle-button="shouldShowInputSectionToggle"
            @activate-section="selectInputSection('recycle')"
            @pattern-changed="onPatternChanged"
          />
        </v-col>
      </v-row>

      <v-row v-if="showRecycleDisposalReport && (manifest.pattern || (manifestDisposal.manifest_recycle_disposal_reports.length > 0))">
        <v-col cols="12">
          <modify-recycle-disposal-report-method
            :manifest="manifest"
            :manifest-disposal="manifestDisposal"
            :can-edit="canEditRecycleDisposalReport"
          />
        </v-col>
      </v-row>
      <!-- /再資源化等情報 -->
    </v-card-text>
  </v-card>
</template>
<script>
import { mapState } from 'vuex'
import ModifyRecycleDisposalReportBasic from './_ModifyRecycleDisposalReportBasic.vue'
import ModifyRecycleDisposalReportMethod from './_ModifyRecycleDisposalReportMethod.vue'

export default {
  name: 'ModifyManifestDisposalReportForm',
  components: {
    ModifyRecycleDisposalReportBasic,
    ModifyRecycleDisposalReportMethod,
  },
  mixins: [],
  props: {
    canEdit: {
      type: Boolean,
      default: () => false,
    },
    manifest: {
      type: Object,
      default: () => {},
    },
    manifestDraft: {
      type: Object,
      default: () => {},
    },
    manifestDisposal: {
      type: Object,
      default: () => {},
    },
    quantityUnits: {
      type: Array,
      default: () => [],
    },
    drivers: {
      type: Array,
      default: () => [],
    },
    users: {
      type: Array,
      default: () => [],
    },
    vehicles: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      activeInputSection: 'disposal',
      disposalReportTypes: [
        { value: 'middle_report', text: '中間' },
        { value: 'last_report', text: '最終' }
      ],
      systemDate: this.$dayjs().format("YYYY-MM-DD"),
      customMessages: {
        double: '数量は半角数字のみ使用できます'
      },
    }
  },
  computed: {
    ...mapState('DriverTenantConfiguration', {
      mode: 'mode',
    }),
    canEditDisposalReport() {
      return this.canEdit && this.activeInputSection === 'disposal'
    },
    canEditRecycleDisposalReport() {
      return this.canEdit && this.activeInputSection === 'recycle'
    },
    shouldShowInputSectionToggle() {
      return this.manifestDisposal.disposal_report_type === 'last_report' && this.showRecycleDisposalReport
    },
    showRecycleDisposalReport() {
      return this.manifestDisposal.disposal_report_type === 'last_report' && this.manifest.recycle_exists && (this.mode === 'transport_disposal' || this.mode === 'disposal')
    }
  },
  watch: {
    showRecycleDisposalReport(newValue) {
      if (!newValue && this.activeInputSection === 'recycle') {
        this.activeInputSection = 'disposal'
      }
    },
    activeInputSection: {
      handler(newValue) {
        this.$emit('inputSectionChangeEvent', newValue)
      },
      immediate: true
    }
  },
  methods: {
    // 報告区分の選択可否を集約判定する
    isDisposalReportTypeSelectionDisabled() {
      return !this.canEditDisposalReport
        || this.isDisposalReportTypeLockedByFinalDisposal()
        || this.isDisposalReportTypeLockedForSecondManifest()
        || this.isDisposalReportTypeLockedAfterLawRevision()
    },
    // 最終処分終了報告まで行われている場合は変更不可
    isDisposalReportTypeLockedByFinalDisposal() {
      return this.manifestDisposal.registered_final_disposal
    },
    // 2次マニフェストは「中間」から「最終」への変更不可
    isDisposalReportTypeLockedForSecondManifest() {
      return this.manifestDisposal.disposal_report_type === 'middle_report'
        && this.manifest.level === 'second_manifest'
    },
    // 法令施行後かつ紙マニフェスト以外は「中間」から「最終」への変更不可
    isDisposalReportTypeLockedAfterLawRevision() {
      return this.manifestDisposal.disposal_report_type === 'middle_report'
        && this.manifest.use_recycling
        && this.manifest.manifest_type !== 'paper_form'
    },
    selectInputSection(section) {
      if (section === 'recycle' && !this.showRecycleDisposalReport) {
        return
      }

      this.activeInputSection = section
    },
    onPatternChanged(patternId) {
      // 処分場処理方法が選択された時の処理
      if (patternId) {
        // patternIdから必要な情報を取得してmanifest.patternを設定
        this.$set(this.manifest, 'pattern', { id: patternId, type: 'recycle' });
      } else {
        // パターンがクリアされた場合
        this.$set(this.manifest, 'pattern', null);
        this.$set(this.manifestDisposal, 'manifest_recycle_disposal_reports', []);
      }
    }
  }
}
</script>

<style scoped>
.section-title-row {
  gap: 8px;
}
</style>
